import React, { useEffect, useRef, useState } from 'react';
import { LoadingPage } from 'ensightful-koala-ui';
import { omit } from 'lodash';
import { styled } from '@mui/material';
import uuid from 'uuid';

import { useAuth } from 'contexts/auth';
import { useChat } from 'contexts/chat';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import chatService from 'services/chat';
import { Box, ChatMessageBlock, Divider, IconButton, RichTextInput, Stack, Text } from 'shell/components';
import { TYPES } from 'utils/constants';
import { uploadFile } from 'utils/file';
import { getS3FileURL, getUserPreferredFullName } from 'utils/helper';

const constructMessageBlocks = (messages, minutesBetweenBlocks = 60) => {
    const blocks = []; // array to hold the subarrays
    let currentBlock = []; // array to hold the messages in the current block
    let prevTimestamp = null; // timestamp of the previous message

    // iterate through each message
    for (const message of messages) {
        const timestamp = message.created_on;

        // if the previous timestamp is null or the time elapsed between
        // the previous and current messages is greater than the specified
        // minutesBetweenBlocks, create a new block and add the current
        // message to it
        if (prevTimestamp === null || timestamp - prevTimestamp > minutesBetweenBlocks * 60 * 1000) {
            if (currentBlock.length > 0) {
                blocks.push(currentBlock); // add the previous block to the array of blocks
            }
            currentBlock = []; // start a new block
        }

        currentBlock.push(message); // add the current message to the current block
        prevTimestamp = timestamp; // update the previous timestamp
    }

    if (currentBlock.length > 0) {
        blocks.push(currentBlock); // add the last block to the array of blocks
    }

    return blocks;
};

const Chat = ({
    activeGroupId,
    channels,
    directs,
    setLatestMessages,
    activeChatId,
    messages,
    setMessages,
    setFiles,
    deleteMessage,
    setAlertDialog,
}) => {
    const { auth } = useAuth();
    const { sendMessage } = useChat();
    const { course, accountUsers } = useUser2();
    const { device } = useViewport();

    const messagesContainerRef = useRef(null);
    const chatBottomRef = useRef(null);
    const inputRef = useRef(null);

    const [isScrolled, setIsScrolled] = useState(false);
    const [scrollHeight, setScrollHeight] = useState(0);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [reply, setReply] = useState(null);

    const activeChat = [...channels, ...directs].find(({ id }) => id === activeChatId);

    const getUserById = (ownerAccountId) => {
        return accountUsers.find(({ owner_account_id }) => owner_account_id === ownerAccountId);
    };

    const handleScroll = async () => {
        const isScrollAtTop = messagesContainerRef.current.scrollTop === 0;
        const isScrollAtBottom =
            messagesContainerRef.current.scrollHeight - messagesContainerRef.current.clientHeight ===
            Math.round(messagesContainerRef.current.scrollTop);

        if (isScrollAtTop) {
            const response = await chatService.listMessages(activeChatId, null, null, messages.length);
            if (response.length) {
                setMessages((current) => [...response, ...current]);
                setScrollHeight(messagesContainerRef.current.scrollHeight);
            }
        }

        if (isScrollAtBottom) {
            setIsScrolled(false);
        } else {
            setIsScrolled(true);
        }
    };

    const handleLoad = () => {
        if (!isScrolled) {
            if (chatBottomRef.current) chatBottomRef.current.scrollIntoView();
        }
    };

    const handleChangeFile = async (e) => {
        if (!isScrolled) chatBottomRef.current.scrollIntoView();
        const timestamp = new Date().getTime();
        const fileArray = Array.from(e.target.files);
        const filesToUpload = fileArray.map((file) => ({
            id: uuid.v4(),
            active: true,
            owner_account_id: auth.owner_account_id,
            course_id: course.id,
            group_id: activeGroupId,
            created_on: timestamp,
            modified_on: timestamp,
            data: {
                name: file.name,
                size: file.size,
                type: file.type,
            },
            type: TYPES.CHAT,
            public: activeChat.name === 'general',
            loading: true,
        }));
        setUploadedFiles((current) => [...current, ...filesToUpload]);
        await Promise.all(
            filesToUpload.map(async (file, index) => {
                uploadFile(
                    e.target.files[index],
                    () => {},
                    (data) => {
                        setUploadedFiles((current) => {
                            return current.map((currentFile) => {
                                if (currentFile.id === file.id) {
                                    return {
                                        ...omit(currentFile, ['loading']),
                                        data: {
                                            ...currentFile.data,
                                            ...data,
                                        },
                                    };
                                } else return currentFile;
                            });
                        });
                    },
                    (e) => {
                        console.error('File upload failed.', e);
                    },
                    auth.owner_account_id
                );
            })
        );
        inputRef.current.props.editor.commands.focus();
    };

    const handleSelect = (key, file) => {
        if (key === 'delete') {
            setUploadedFiles((current) => current.filter(({ id }) => id !== file.id));
        }
    };

    const handleSend = (html, reply) => {
        if (!html.trim() && !uploadedFiles.length) return;
        const timestamp = new Date().getTime();
        const message = {
            id: uuid.v4(),
            active: true,
            created_on: timestamp,
            modified_on: timestamp,
            course_id: auth.course_id,
            group_id: activeGroupId,
            sender_account_id: auth.owner_account_id,
            channel_id: activeChatId,
            message_type: activeChat.chat_type,
            message_text: html.trim(),
        };
        if (uploadedFiles.length) {
            message.file = uploadedFiles[0];
            message.file_id = uploadedFiles[0].id;
            if (uploadedFiles[0].data.type.startsWith('image')) {
                message.type = 'photo';
                message.photoURL = getS3FileURL(uploadedFiles[0].data);
            } else {
                message.type = 'file';
                message.fileURL = getS3FileURL(uploadedFiles[0].data);
            }
            setUploadedFiles([]);
            setFiles((current) => [...current, uploadedFiles[0]]);
        }
        if (reply) {
            message.replied_message_id = reply.id;
            message.replied_message = reply;
            setReply(null);
        }
        setMessages((current) => [...current, message]);
        setLatestMessages((current) => ({
            ...current,
            [activeChatId]: message,
        }));
        sendMessage(omit(message, ['replied_message']));
    };

    // scroll to bottom when a new message is sent or received
    useEffect(() => {
        if (chatBottomRef.current) {
            if (messages[messages?.length - 1]?.sender_account_id === auth.owner_account_id) {
                chatBottomRef.current.scrollIntoView(false);
            } else if (!isScrolled) {
                chatBottomRef.current.scrollIntoView(false);
            }
        }
    }, [isScrolled, messages, auth.owner_account_id]);

    // scroll to bottom when a chat is first opened
    useEffect(() => {
        if (chatBottomRef.current) {
            chatBottomRef.current.scrollIntoView();
        }
    }, [activeChatId]);

    // set scroll position when older messages are fetched
    useEffect(() => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight - scrollHeight;
        }
    }, [scrollHeight]);

    useEffect(() => {
        setIsScrolled(false);
        setScrollHeight(0);
        setUploadedFiles([]);
        setReply(null);
    }, [activeChatId]);

    if (!messages) {
        return <LoadingPage />;
    }

    const renderMessages = () => {
        return constructMessageBlocks(messages.sort((a, b) => a.created_on - b.created_on)).map(
            (messageBlock, index) => (
                <ChatMessageBlock
                    key={index}
                    messages={messageBlock}
                    getUserById={getUserById}
                    selfOwnerAccountId={auth.owner_account_id}
                    showSenderName={activeChat.chat_type === 'channel'}
                    onClickDelete={(message) => {
                        setAlertDialog((current) => ({
                            ...current,
                            open: true,
                            message: 'Delete Message?',
                            description: 'Are you sure you want to delete this message?',
                            variant: 'destructive',
                            actionButtonText: 'Delete',
                            cancelButtonText: 'Cancel',
                            onAction: () => {
                                deleteMessage(message);
                                setAlertDialog((current) => ({ ...current, open: false }));
                            },
                        }));
                    }}
                    onClickReply={(message) => {
                        setReply({
                            ...message,
                            owner_account_id: message.sender_account_id,
                            text: message.message_text,
                        });
                        inputRef.current.props.editor.commands.focus();
                    }}
                    onLoad={handleLoad}
                />
            )
        );
    };

    return (
        <CustomStack device={device} justifyContent={'space-between'}>
            <MessagesStack ref={messagesContainerRef} spacing={2} onScroll={handleScroll}>
                {renderMessages()}
                <Box ref={chatBottomRef} />
            </MessagesStack>
            <Box sx={{ p: 4, pt: 0 }}>
                <RichTextInput
                    ref={inputRef}
                    disableSend={uploadedFiles.some(({ loading }) => loading)}
                    files={uploadedFiles}
                    getNameById={(id) => {
                        const user = getUserById(id);
                        return user ? getUserPreferredFullName(user) : '[Removed]';
                    }}
                    isReplyingTo={reply}
                    onChangeFile={handleChangeFile}
                    onClickCancelReply={() => setReply(null)}
                    onSelect={({ key }, file) => handleSelect(key, file)}
                    onSend={handleSend}
                />
            </Box>
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ device }) => {
    return {
        height: device === 'mobile' ? 'calc(100vh - 64px - 48px - 1px)' : 'calc(100vh - 64px - 20px - 48px - 1px)',
    };
});

const MessagesStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(0, 4, 4),
        overflowX: 'auto',
    };
});

const MessagesSection = ({
    activeGroupId,
    channels,
    directs,
    setLatestMessages,
    activeChatId,
    messages,
    setMessages,
    setFiles,
    deleteMessage,
    setAlertDialog,
    channelDetailsOpen,
    setChannelDetailsOpen,
    onClickBack,
}) => {
    const { auth } = useAuth();
    const { accountUsers } = useUser2();
    const { device } = useViewport();

    const getChatTitle = (chat) => {
        if (chat.chat_type === 'channel') {
            return `#${chat.name}`;
        }
        const correspondentId = chat.members.find((id) => id !== auth.owner_account_id);
        const correspondent = accountUsers.find(({ owner_account_id }) => owner_account_id === correspondentId);
        return getUserPreferredFullName(correspondent);
    };

    if (!activeGroupId) {
        return (
            <Box sx={{ flex: 1 }}>
                <Text sx={{ mt: '40vh' }} align={'center'} color={'light'} variant={'h6'}>
                    Please Select a Team
                </Text>
            </Box>
        );
    }

    if (!channels || !directs || !activeChatId) {
        return <LoadingPage style={{ marginTop: '50%', width: '100%', height: '100%' }} />;
    }

    const activeChat = [...channels, ...directs].find(({ id }) => id === activeChatId);

    return (
        <RootBox channelDetailsOpen={channelDetailsOpen} device={device}>
            <Stack sx={{ p: 2 }} alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={4}>
                <Stack sx={{ minWidth: 0 }} alignItems={'center'} direction={'row'} spacing={2}>
                    {device === 'mobile' && (
                        <IconButton
                            aria-label={'back'}
                            kind={'arrowLeft'}
                            size={'medium'}
                            variant={'square'}
                            onClick={onClickBack}
                        />
                    )}
                    <Text noWrap title={getChatTitle(activeChat)} variant={'sh1'}>
                        {getChatTitle(activeChat)}
                    </Text>
                </Stack>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    {activeChat.chat_type === 'channel' && (
                        <Text sx={{ whiteSpace: 'nowrap' }} variant={'p2'}>
                            {activeChat.members.length} members
                        </Text>
                    )}
                    <IconButton
                        data-cy={'channel-detail-arrow-button'}
                        aria-label={channelDetailsOpen ? 'close channel details' : 'open channel details'}
                        kind={channelDetailsOpen ? 'chevronRight' : 'chevronLeft'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => setChannelDetailsOpen((current) => !current)}
                    />
                </Stack>
            </Stack>
            <Divider />
            <Chat
                activeGroupId={activeGroupId}
                channels={channels}
                directs={directs}
                setLatestMessages={setLatestMessages}
                activeChatId={activeChatId}
                messages={messages}
                setMessages={setMessages}
                setFiles={setFiles}
                deleteMessage={deleteMessage}
                setAlertDialog={setAlertDialog}
            />
        </RootBox>
    );
};

const RootBox = styled(Box)(({ channelDetailsOpen, device }) => {
    return {
        flex: 1,
        minWidth: 0,
        ...(device !== 'mobile' &&
            channelDetailsOpen && {
                maxWidth: 'calc(100% - 229px - 229px - 1px - 1px)',
            }),
    };
});

export default MessagesSection;
